import { ChangeDetectionStrategy, Component, effect, signal, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { WorkflowActionInputComponent } from "./workflow.action.input.component";
import { SystemActionComponent } from "./abstract.system.action.component";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-add-org-categories-action",
    template: `
        <div class="form-group">
            <small class="form-text auto-collect-info">
                Categories from ORG_CATEGORIES form fields are automatically collected. Optionally add static categories
                or a boolean gate below.
            </small>
        </div>

        @if (loading()) {
            <div class="form-group">
                <em>Loading...</em>
            </div>
        } @else {
            <div class="form-group">
                <label class="checkbox-option">
                    <input type="checkbox" [checked]="showStaticCategories()" (change)="onToggleStaticCategories()" />
                    Add specific organization categories
                </label>
                @if (showStaticCategories()) {
                    <div class="sub-section">
                        <s25-ng-multiselect-search-criteria
                            [type]="'organizationCategories'"
                            [selectedItems]="selectedCategories()"
                            (changed)="onCategoriesChange($event)"
                            [showResults]="true"
                        />
                        <small class="form-text">
                            Select categories to always add to the organization when this action runs.
                        </small>
                    </div>
                }
            </div>

            <div class="form-group">
                <label class="checkbox-option">
                    <input type="checkbox" [checked]="showBooleanGate()" (change)="onToggleBooleanGate()" />
                    Boolean gate
                </label>
                @if (showBooleanGate()) {
                    <div class="sub-section">
                        <s25-ng-workflow-action-input
                            [value]="inputMap()['booleanField']?.inputValue || ''"
                            (valueChange)="setInputValue('booleanField', $event)"
                            [label]="'Boolean Field Reference'"
                            [type]="'text'"
                            [required]="false"
                            [help]="'If this boolean field is false, the entire action is skipped.'"
                            [acceptedAnswerTypes]="acceptedFor('booleanField')"
                        />
                    </div>
                    <div class="sub-section">
                        <label>Categories to Add (when boolean is true)</label>
                        <s25-ng-multiselect-search-criteria
                            [type]="'organizationCategories'"
                            [selectedItems]="booleanCategories()"
                            (changed)="onBooleanCategoriesChange($event)"
                            [showResults]="true"
                        />
                        <small class="form-text">Select categories to add when the boolean field is true.</small>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }

        .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 14px;
        }

        .form-text {
            color: #666;
            font-size: 12px;
            margin-top: 4px;
            display: block;
        }

        .auto-collect-info {
            color: #555;
            font-size: 13px;
            font-style: italic;
        }

        .checkbox-option {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            font-weight: normal;
        }

        .checkbox-option input[type="checkbox"] {
            margin: 0;
        }

        .sub-section {
            margin-top: 8px;
            margin-left: 24px;
        }

        .sub-section label {
            font-weight: 500;
            color: #555;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, S25MultiselectModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddOrgCategoriesActionComponent extends SystemActionComponent {
    protected readonly actionCode = "ADD_ORG_CATEGORIES" as const;
    readonly showStaticCategories = signal(false);
    readonly showBooleanGate = signal(false);
    readonly selectedCategories = signal<S25ItemI[]>([]);
    readonly booleanCategories = signal<S25ItemI[]>([]);
    readonly loading = signal<boolean>(true);
    private initialized = false;

    constructor() {
        super();

        effect(() => {
            const inputs = this.inputMap();
            if (this.initialized) return;
            this.initialized = true;
            this.initializeFromInputs(inputs).finally(() => this.loading.set(false));
        });
    }

    private async initializeFromInputs(inputs: Record<string, { inputValue?: string } | undefined>) {
        const categoryIds = inputs["orgCategoryIds"]?.inputValue;
        if (categoryIds) {
            this.showStaticCategories.set(true);
            this.selectedCategories.set(await this.parseCategoryIdsAndFetchNames(categoryIds));
        }

        if (inputs["booleanField"]?.inputValue) {
            this.showBooleanGate.set(true);
            const booleanCategoryIds = inputs["booleanCategoryIds"]?.inputValue;
            if (booleanCategoryIds) {
                this.booleanCategories.set(await this.parseCategoryIdsAndFetchNames(booleanCategoryIds));
            }
        }
    }

    private async parseCategoryIdsAndFetchNames(json: string): Promise<S25ItemI[]> {
        try {
            const categoryIds: number[] = JSON.parse(json);
            if (!categoryIds.length) return [];

            const categories = await SearchCriteriaService.getOrganizationCategories();
            const categoryMap = new Map<number, string>(
                categories.map((cat: any) => [cat.itemId, String(cat.itemName || "")]),
            );

            return categoryIds.map((id) => ({
                itemId: id,
                itemName: (categoryMap.get(id) as string | undefined) || `Category ${id}`,
            }));
        } catch {
            return [];
        }
    }

    onToggleStaticCategories() {
        const show = !this.showStaticCategories();
        this.showStaticCategories.set(show);
        if (!show) {
            this.setInputValue("orgCategoryIds", "");
            this.selectedCategories.set([]);
        }
    }

    onToggleBooleanGate() {
        const show = !this.showBooleanGate();
        this.showBooleanGate.set(show);
        if (!show) {
            this.setInputValue("booleanField", "");
            this.setInputValue("booleanCategoryIds", "");
            this.booleanCategories.set([]);
        }
    }

    onCategoriesChange(items: S25ItemI[]) {
        this.selectedCategories.set(items);
        this.setInputValue("orgCategoryIds", JSON.stringify(items.map((item) => item.itemId)));
    }

    onBooleanCategoriesChange(items: S25ItemI[]) {
        this.booleanCategories.set(items);
        this.setInputValue("booleanCategoryIds", JSON.stringify(items.map((item) => item.itemId)));
    }
}
